/**
 * The checkbox in front of a callable row, and the one over the column head.
 *
 * `relative z-[1]` on both, and it is not decoration: every list in this CRM
 * puts a stretched `::after` over the whole row so the name is clickable end to
 * end, and a checkbox under it is a checkbox that opens the row instead of
 * ticking. The same reason the mail links in these tables carry it.
 *
 * A row with no phone number gets a disabled box rather than none at all. An
 * empty cell in a column of checkboxes reads as a rendering fault; a greyed one
 * with a tooltip says the one true thing about that row.
 */
import { Checkbox } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { CallTarget } from "../callState";
import type { QueuePicks } from "./useQueuePicks";

export function QueuePick({
  picks,
  id,
  target,
}: {
  picks: QueuePicks;
  /** This row's key in the selection - the domain, the registry code. */
  id: string;
  /** What dialling this row means, or null when it carries no number. */
  target: CallTarget | null;
}) {
  const t = useT();
  return (
    <span className="relative z-[1] flex items-center pl-3">
      <Checkbox
        checked={picks.has(id)}
        disabled={!target}
        onCheckedChange={() => target && picks.toggle(id, target)}
        aria-label={t("calls.queue.pick")}
        title={target ? undefined : t("calls.queue.noNumber")}
      />
    </span>
  );
}

export function QueuePickAll({
  picks,
  entries,
}: {
  picks: QueuePicks;
  /** Every callable row currently on screen. */
  entries: ReadonlyArray<{ key: string; target: CallTarget }>;
}) {
  const t = useT();
  const on = entries.length > 0 && entries.every((e) => picks.has(e.key));
  // "Some" is its own state on this box (see the shared Checkbox): a page where
  // three of twenty are ticked must not draw the same empty box as a page where
  // none are, or the head reads as a control that did nothing.
  const some = !on && entries.some((e) => picks.has(e.key));
  return (
    <span className="relative z-[1] flex items-center pl-3">
      <Checkbox
        checked={some ? "indeterminate" : on}
        disabled={entries.length === 0}
        onCheckedChange={() => picks.setMany(entries, !on)}
        aria-label={t("calls.queue.pickPage")}
      />
    </span>
  );
}
